"use client";

import { Activity, Brain, Compass, Flame, ChartLine as LineChart, Radar } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TechnicalOverview() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const capabilities = [{
    number: "01",
    title: "Advanced Material Analysis",
    description: "Precision testing of phenolic resin structures, ensuring structural integrity and thermal resistance for industrial applications.",
    Icon: LineChart
  }, {
    number: "02",
    title: "Molecular Bonding Research",
    description: "Deep-dive analysis into polymer chains to optimize durability and chemical stability in high-stress environments.",
    Icon: Brain
  }, {
    number: "03",
    title: "Performance Benchmarking",
    description: "Rigorous comparative testing against industry standards to validate superior heat dissipation and mechanical strength.",
    Icon: Radar
  }, {
    number: "04",
    title: "Thermal Mapping Solutions",
    description: "Visualizing heat distribution across composite surfaces to identify potential failure points and optimize design geometry.",
    Icon: Compass
  }, {
    number: "05",
    title: "Durability Modeling",
    description: "Predictive lifecycle simulations based on environmental exposure, stress cycles, and chemical degradation factors.",
    Icon: Activity
  }, {
    number: "06",
    title: "Application Engineering",
    description: "Tailored formulation development for specific aerospace, automotive, and electrical insulation requirements.",
    Icon: Flame
  }];
  return <section data-section-id="4553" id="technical-overview" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-5">
            <p className="font-mono text-xs tracking-widest uppercase text-primary mb-6">
              03 / Technical Capabilities
            </p>
            <h2 className="font-default font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
              Phenolic engineering, perfected.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8 flex items-end">
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground/90 max-w-2xl">
              Our technical framework combines advanced material science with rigorous laboratory validation. Each capability serves as a critical pillar in our commitment to delivering high-performance phenolic solutions for the most demanding industrial sectors.
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
          {capabilities.map((cap, i) => {
          const Icon = cap.Icon;
          return <div data-index={i} key={cap.number} className="group relative bg-card border border-border transition-all duration-500 ease-out p-8 sm:p-10 flex flex-col" style={{
            aspectRatio: "4 / 5",
            minHeight: "420px"
          }}>
                {}
                <span className="font-mono text-xs tracking-widest text-primary">
                  {cap.number}
                </span>

                {}
                <div className="relative flex items-center justify-start mt-10 mb-10">
                  <div aria-hidden="true" className="absolute -inset-6 rounded-lg bg-primary/10 blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
                  <Icon strokeWidth={1} className="relative size-8 text-foreground group-hover:text-primary transition-colors duration-500" />
                </div>

                {}
                <h3 className="font-default font-normal text-2xl leading-snug text-foreground mb-4">
                  {cap.title}
                </h3>

                {}
                <p className="font-default text-[15px] leading-relaxed text-muted-foreground mb-8">
                  {cap.description}
                </p>

                {}
                <div className="mt-auto">
                  <Link to="/ServicesPage" className="inline-flex items-center gap-2 font-mono text-xs tracking-widest uppercase text-foreground hover:text-primary transition-colors duration-300">
                    <span className="relative">
                      <span className="bg-left-bottom bg-gradient-to-r from-primary to-primary bg-[length:0%_1px] bg-no-repeat group-hover:bg-[length:100%_1px] transition-[background-size] duration-500 ease-out pb-0.5">
                        Learn More
                      </span>
                    </span>
                    <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </div>;
        })}
        </div>

        {}
        <div className="mt-16 md:mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-border">
          
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-widest uppercase rounded-none">
            
          </Button>
        </div>
      </div>
    </section>;
}